import en from "../../locales/en.json" with { type: "json" };
import zh from "../../locales/zh.json" with { type: "json" };
import { overlay } from "./copy-parity";

export const LOCALES = ["en", "zh"] as const;
export type AppLocale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: AppLocale = "en";

const zhOverEn = overlay(en, zh);

/**
 * One resource set for every client (WEB-2, card 095): an entry Chinese lacks reads in English. `pnpm lint` keeps the
 * two files alike, so this covers copy changed between a lint and a release.
 */
export const messages = { en, zh: zhOverEn.copy } as const;
export type Messages = typeof en;

/** The entries each language shows in English for want of its own. */
export const copyFallbacks: Readonly<Record<AppLocale, readonly string[]>> = {
  en: [],
  zh: zhOverEn.fellBack,
};

const loggedLocales = new Set<AppLocale>();

/** Logs, once per process and language, each entry shown in English instead (WEB-2). */
export function logCopyFallbacks(locale: AppLocale, log: (message: string) => void = console.warn): void {
  if (loggedLocales.has(locale)) return;
  loggedLocales.add(locale);
  for (const key of copyFallbacks[locale]) log(`Copy: "${key}" has no ${locale} text; English is shown`);
}

const reported = new Set<string>();

/**
 * use-intl's and next-intl's `onError`: each problem logged once, such as an entry missing in both languages, whose
 * key the reader then sees. Without it every render would log again.
 */
export function onCopyError(
  error: { readonly code: string; readonly message: string },
  log: (message: string) => void = console.error,
): void {
  const key = `${error.code}:${error.message}`;
  if (reported.has(key)) return;
  reported.add(key);
  log(`Copy: ${error.message}`);
}

export function isAppLocale(value: string | undefined): value is AppLocale {
  return value !== undefined && (LOCALES as readonly string[]).includes(value);
}

/** Picks zh for Chinese browser languages, en otherwise. */
export function detectLocale(languages: readonly string[]): AppLocale {
  return languages.some((language) => language.toLowerCase().startsWith("zh")) ? "zh" : DEFAULT_LOCALE;
}
